import { useEffect, useRef } from "react";
import { useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { useStores } from "stores/setup/use-store";

/**
 * T025: Error Notification Hook
 *
 * Displays toast notifications when Rainbow AI radar encounters errors.
 * - Shows user-friendly error messages from i18n
 * - Auto-dismisses after 5 seconds
 * - Only shows once per error (prevents spam)
 * - Uses Chakra UI toast for consistent UX
 */
export const useRadarErrorNotification = () => {
  const {
    dataStores: { radarMapStore },
  } = useStores();
  const toast = useToast();
  const { t } = useTranslation("translation", {
    keyPrefix: "radarLegend",
  });
  const lastErrorRef = useRef<string | null>(null);

  useEffect(() => {
    const errorMessage = radarMapStore.errorMessage;

    // Only show toast if there's a new error (different from last one)
    if (errorMessage && errorMessage !== lastErrorRef.current) {
      lastErrorRef.current = errorMessage;

      toast({
        title: t("error.title"),
        description: t(errorMessage),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-right",
      });
    }

    // Clear last error ref when error is resolved
    if (!errorMessage) {
      lastErrorRef.current = null;
    }
  }, [radarMapStore.errorMessage, toast, t]);
};
